import { z } from "zod";
import { addToast } from "../../../../toasts";
import { importService } from "../../../../services/importService";
import { ImportsModalCtx } from "../../ImportsModalCtx";
import { disableButton, enableButton } from "../../../../lib/utility";
import { createFormFieldHandler } from "../../../../utils/form-handler";
import { HOW_TO_IMPORT_SOCIAL_FEEDS_OR_REVIEWS } from "../../../../utils/helper-url";
 
export function GetOYOReviewsForm(
    { displayFeeds }: { displayFeeds: (data: object) => void },
    { resetSections }: { resetSections: () => void },
    { showSkeleton }: { showSkeleton: (enable: boolean) => void },
) {

    const formSchema = z.object({
        search:z.string().min(1, "Business name is required").refine((val) => val.trim().length > 0, "Business name cannot be only spaces"),
        sort: z.string(),
    });

    type FormData = z.infer<typeof formSchema>;
    let $formElem: null | HTMLFormElement = null;
    let $submitButton: null | HTMLButtonElement = null;

    function resetForm() {
        $formElem!.reset();
        ImportsModalCtx.setImportProcessRunning(false);
    }

    function init() {
        $formElem = document.getElementById("get-oyo-reviews-form") as HTMLFormElement;
        $submitButton = $formElem.querySelector('#btn-get-oyo-reviews') as HTMLButtonElement;
        if (!$formElem) {
            console.error("Can't find all the form elements!");
        }

        const { setFieldError, validateOnBlur } = createFormFieldHandler(
            formSchema,
            $formElem
        );

        // Add event listeners to form fields to validate on blur
        validateOnBlur(); 

        // Handle form submission
        $formElem.addEventListener("submit", async (e) => {
            e.preventDefault();
            if (ImportsModalCtx.getImportProcessRunning()) {
                addToast({ toastBody: "Please wait for the current import process to finish" }, "default", "error");
                return;
            }
            const formData = new FormData($formElem!);
            const data = Object.fromEntries(formData.entries()) as FormData;

            try {
                formSchema.parse(data);
                disableButton($submitButton!, false);
                resetSections();
                showSkeleton(true);
                try {
                    ImportsModalCtx.setImportProcessRunning(true);
                    const getFeedResp = await importService.getFeed('/oyo', {
                        search: data.search,
                        extra_parameters: {
                            sort: data.sort
                        }
                    });
                    if (getFeedResp.data && getFeedResp.data.error) {
                        throw {
                            errors: {
                                search: [getFeedResp.data.error]
                            },
                            message: getFeedResp.data.error
                        }
                    }
                    ImportsModalCtx.showErrorImage($formElem!,true,false);
                    displayFeeds(getFeedResp);
                } catch (error: any) {
                    ImportsModalCtx.showErrorImage($formElem!);
                    showSkeleton(false);
                    // this catch block is for handling Server errors on the request
                    console.error("Get import review request failed: ", error);
                    addToast({ toastBody: error.message }, "default", "error");
                    error.errors && Object.keys(error.errors).forEach((field) => {
                        // Display each error message for the corresponding field
                        setFieldError(field as keyof FormData, error.errors[field][0]);
                    });
                } finally {
                    ImportsModalCtx.setImportProcessRunning(false);
                }
            } catch (error) {
                // this catch block is for handling Frontend form validation
                if (error instanceof z.ZodError) {
                  error.errors.forEach((err) => {
                    setFieldError(err.path[0] as keyof FormData, err.message);
                  });
                }
            } finally {
                enableButton($submitButton!);
            }
        });
    }

    function render() {
        return /*html*/`
        <form id="get-oyo-reviews-form">
            <div class="mt-2 max-w-full">
                <div class="flex flex-col mob:flex-row items-start gap-4 mob:gap-2 max-w-full">
                    <input type="text"
                        class="py-2.5 px-3 block w-full border-gray-200 rounded-lg text-sm placeholder:text-gray-400 focus:border-blue-500 focus:ring-blue-500 disabled:opacity-50 disabled:pointer-events-none dark:bg-transparent dark:border-neutral-700 dark:text-neutral-300 dark:placeholder:text-white/60 dark:focus:ring-neutral-600"
                        name="search"
                        placeholder="Enter your OYO URL">

                    <select id="hs-hidden-select"
                        name="sort"
                        class="mob:max-w-[150px] cursor-pointer py-2.5 px-2 pe-9 block w-full border-gray-200 rounded-lg text-sm focus:border-blue-500 focus:ring-blue-500 disabled:opacity-50 disabled:pointer-events-none dark:bg-transparent dark:border-neutral-700 dark:text-neutral-400 dark:placeholder-neutral-500 dark:focus:ring-neutral-600">
                        <option value="relevance" selected="">Relevance</option>
                        <option value="date-high">Recent reviews</option>
                        <option value="rating-high">High to low ratings</option>
                        <option value="rating-low">Low to high ratings</option>
                    </select>

                    <button type="submit" id="btn-get-oyo-reviews"
                        class="whitespace-nowrap py-2.5 px-3 inline-flex items-center gap-x-1 text-sm font-medium rounded-lg border border-transparent bg-blue-600 text-white hover:bg-blue-700 focus:outline-none focus:bg-blue-700 disabled:opacity-50 disabled:pointer-events-none">
                        Get Reviews
                    </button>
                </div>
            </div>
            <p class="text-gray-500 text-xs font-normal font-['Inter'] mt-1 dark:text-neutral-500">
                e.g. https://www.oyorooms.com/237350
            </p>
            <p class="text-gray-500 text-xs font-normal font-['Inter'] mt-1 dark:text-neutral-500 hidden">
                Need Help importing review? <a href="${HOW_TO_IMPORT_SOCIAL_FEEDS_OR_REVIEWS}" target="_blank" class="text-blue-600 hover:text-blue-800">Click Here</a>
            </p>
        </form>
        `;
    }

    return {
        init,
        render,
        resetForm
    };
}